import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Package, Users, ShoppingCart, Clock, Check, Truck, 
    X, AlertCircle, RefreshCw, Filter, Search, ChevronDown,
    BarChart3, Loader2, CheckCircle, XCircle, ClipboardList,
    Printer, Download, ChevronRight, MapPin, Scale, Save, Camera,
    ArrowUp, ArrowDown, Minus, UploadCloud, FileSpreadsheet, DollarSign, History, FileDown,
    CreditCard, Send, Timer, AlertTriangle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
    Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle,
} from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---- Order grouping helpers (by placement timestamp: submitted_at || created_at) ----
const pad2 = (n) => String(n).padStart(2, '0');
const isoDay = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const placementDate = (o) => new Date(o.submitted_at || o.created_at || 0);
const startOfWeek = (d) => {
    const x = new Date(d);
    const dow = (x.getDay() + 6) % 7; // Monday = 0
    x.setHours(0, 0, 0, 0);
    x.setDate(x.getDate() - dow);
    return x;
};
const orderGroupInfo = (d, mode) => {
    if (mode === 'month') {
        return {
            key: `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`,
            label: d.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' }),
            sortTs: new Date(d.getFullYear(), d.getMonth(), 1).getTime(),
        };
    }
    if (mode === 'week') {
        const mon = startOfWeek(d);
        const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
        const md = { day: 'numeric', month: 'short' };
        return {
            key: isoDay(mon),
            label: `Week of ${mon.toLocaleDateString('en-AU', md)} – ${sun.toLocaleDateString('en-AU', { ...md, year: 'numeric' })}`,
            sortTs: mon.getTime(),
        };
    }
    const day0 = new Date(d); day0.setHours(0, 0, 0, 0);
    return {
        key: isoDay(d),
        label: d.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }),
        sortTs: day0.getTime(),
    };
};

export default function AdminPage() {
    const navigate = useNavigate();
    const { user, isAuthenticated, loading: authLoading } = useAuth();
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [orders, setOrders] = useState([]);
    const [products, setProducts] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [pickingList, setPickingList] = useState(null);
    const [statusFilter, setStatusFilter] = useState('all');
    const [orderSearch, setOrderSearch] = useState('');
    const [orderGrouping, setOrderGrouping] = useState('day');
    const [collapsedGroups, setCollapsedGroups] = useState({}); // { [groupKey]: true } => collapsed
    const toggleGroup = (key) => setCollapsedGroups(prev => ({ ...prev, [key]: !prev[key] }));
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [productSearch, setProductSearch] = useState('');
    const [productCategory, setProductCategory] = useState('all');
    const [pickingStatusFilter, setPickingStatusFilter] = useState('pending,confirmed,preparing');
    const [activeTab, setActiveTab] = useState('orders');
    const [now, setNow] = useState(Date.now());
    const [resendingId, setResendingId] = useState(null);
    const unpaidRef = useRef(null);

    // Live-ticking clock so "unpaid for" / "expires in" timers stay current.
    useEffect(() => {
        const t = setInterval(() => setNow(Date.now()), 30000);
        return () => clearInterval(t);
    }, []);

    useEffect(() => {
        if (!authLoading && !isAuthenticated) {
            navigate('/admin');
            return;
        }

        if (isAuthenticated && user && !user.is_admin) {
            toast.error('Admin access required');
            navigate('/admin');
            return;
        }

        if (isAuthenticated && user?.is_admin) {
            fetchData();
        }
    }, [isAuthenticated, authLoading, user, navigate]);

    const fetchData = async () => {
        try {
            const [statsRes, ordersRes, productsRes, contactsRes, pickingRes] = await Promise.all([
                axios.get(`${API}/admin/stats`, { withCredentials: true }),
                axios.get(`${API}/admin/orders`, { params: { limit: 1000 }, withCredentials: true }),
                axios.get(`${API}/admin/products`, { withCredentials: true }),
                axios.get(`${API}/admin/contacts`, { withCredentials: true }),
                axios.get(`${API}/admin/picking-list?status=${pickingStatusFilter}`, { withCredentials: true })
            ]);
            setStats(statsRes.data);
            setOrders(ordersRes.data);
            setProducts(productsRes.data);
            setContacts(contactsRes.data);
            setPickingList(pickingRes.data);
        } catch (error) {
            console.error('Error fetching admin data:', error);
            if (error.response?.status === 403) {
                toast.error('Admin access required');
                navigate('/dashboard');
            }
        } finally {
            setLoading(false);
        }
    };

    const fetchOrders = async (search = '') => {
        try {
            const params = { limit: 1000 };
            if (search) params.search = search;
            const res = await axios.get(`${API}/admin/orders`, { params, withCredentials: true });
            setOrders(res.data);
        } catch (error) {
            console.error('Error fetching orders:', error);
        }
    };

    const refreshData = async () => {
        await fetchData();
        if (orderSearch) fetchOrders(orderSearch);
    };

    useEffect(() => {
        const t = setTimeout(() => { fetchOrders(orderSearch); }, 350);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderSearch]);

    const fetchPickingList = async () => {
        try {
            const res = await axios.get(`${API}/admin/picking-list?status=${pickingStatusFilter}`, { withCredentials: true });
            setPickingList(res.data);
        } catch (error) {
            console.error('Error fetching picking list:', error);
            toast.error('Failed to load picking list');
        }
    };

    const updateOrderStatus = async (orderId, newStatus) => {
        try {
            await axios.put(
                `${API}/admin/orders/${orderId}/status`,
                { status: newStatus },
                { withCredentials: true }
            );
            toast.success('Order status updated');
            refreshData();
        } catch (error) {
            toast.error('Failed to update order');
        }
    };

    const resendPayment = async (orderId) => {
        setResendingId(orderId);
        try {
            await axios.post(
                `${API}/admin/orders/${orderId}/resend-payment`,
                { origin_url: window.location.origin },
                { withCredentials: true }
            );
            toast.success('A fresh payment link has been emailed to the customer');
            await refreshData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to resend payment link');
        } finally {
            setResendingId(null);
        }
    };

    const jumpToUnpaid = () => {
        setActiveTab('orders');
        setStatusFilter('ready');
        setTimeout(() => unpaidRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    };

    const toggleProductAvailability = async (productId, available) => {
        try {
            await axios.put(
                `${API}/admin/products/${productId}/availability`,
                { available },
                { withCredentials: true }
            );
            toast.success(`Product ${available ? 'enabled' : 'disabled'}`);
            // Update local state
            setProducts(products.map(p => 
                p.product_id === productId ? { ...p, available } : p
            ));
        } catch (error) {
            toast.error('Failed to update product');
        }
    };

    const updateStockQuantity = async (productId, quantity) => {
        try {
            await axios.put(
                `${API}/admin/products/${productId}/stock`,
                { stock_quantity: quantity },
                { withCredentials: true }
            );
            // Update local state
            setProducts(products.map(p => 
                p.product_id === productId ? { ...p, stock_quantity: quantity } : p
            ));
        } catch (error) {
            toast.error('Failed to update stock');
        }
    };

    const uploadProductImage = async (productId, file) => {
        const formData = new FormData();
        formData.append('file', file);
        try {
            const res = await axios.post(
                `${API}/admin/products/${productId}/image/upload`,
                formData,
                { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
            );
            toast.success('Photo updated');
            setProducts(products.map(p =>
                p.product_id === productId ? { ...p, image_url: res.data.image_url, image_is_custom: true } : p
            ));
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to upload photo');
        }
    };

    const filteredOrders = orders.filter(order => {
        if (!(statusFilter === 'all' || order.status === statusFilter)) return false;
        const d = placementDate(order);
        if (dateFrom) { const f = new Date(dateFrom); f.setHours(0, 0, 0, 0); if (d < f) return false; }
        if (dateTo) { const t = new Date(dateTo); t.setHours(23, 59, 59, 999); if (d > t) return false; }
        return true;
    });

    const groupedOrders = useMemo(() => {
        const groups = new Map();
        for (const o of filteredOrders) {
            const info = orderGroupInfo(placementDate(o), orderGrouping);
            if (!groups.has(info.key)) groups.set(info.key, { ...info, orders: [], total: 0 });
            const g = groups.get(info.key);
            g.orders.push(o);
            if (o.status !== 'cancelled') {
                g.total += (o.actual_total != null ? o.actual_total : (o.total != null ? o.total : 0));
            }
        }
        return Array.from(groups.values()).sort((a, b) => b.sortTs - a.sortTs);
    }, [filteredOrders, orderGrouping]);

    const collapseAllGroups = () => {
        const all = {};
        groupedOrders.forEach(g => { all[g.key] = true; });
        setCollapsedGroups(all);
    };
    const expandAllGroups = () => setCollapsedGroups({});

    // Unpaid = payment link sent (status 'ready') but not yet paid. Oldest first (chase priority).
    const unpaidOrders = useMemo(() => {
        const sentOf = (o) => new Date(o.payment_link_sent_at || o.updated_at || o.created_at || 0).getTime();
        return orders
            .filter(o => o.status === 'ready' && o.payment_status !== 'paid')
            .sort((a, b) => sentOf(a) - sentOf(b));
    }, [orders]);

    const filteredProducts = products.filter(product => {
        const matchesSearch = !productSearch || 
            product.name.toLowerCase().includes(productSearch.toLowerCase());
        const matchesCategory = productCategory === 'all' || 
            product.category === productCategory;
        return matchesSearch && matchesCategory;
    });

    const categories = [...new Set(products.map(p => p.category))];

    if (authLoading || loading) {
        return (
            <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-[#1B4D3E]" />
            </div>
        );
    }

    const statusColors = {
        pending: 'bg-yellow-100 text-yellow-700',
        confirmed: 'bg-blue-100 text-blue-700',
        preparing: 'bg-purple-100 text-purple-700',
        ready: 'bg-green-100 text-green-700',
        paid: 'bg-emerald-100 text-emerald-700',
        dispatched: 'bg-blue-100 text-blue-700',
        delivered: 'bg-slate-100 text-slate-700',
        cancelled: 'bg-red-100 text-red-700'
    };
    const SUMMARY_STATUSES = ['pending', 'preparing', 'ready', 'paid', 'dispatched', 'delivered', 'cancelled'];

    return (
        <div className="min-h-screen bg-[#F8F9FA] pb-8" data-testid="admin-page">
            <div className="container-main py-6 md:py-8">
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-slate-900">
                            Admin Dashboard
                        </h1>
                        <p className="text-slate-500">Manage orders and inventory</p>
                    </div>
                    <Button onClick={fetchData} variant="outline" className="gap-2">
                        <RefreshCw className="w-4 h-4" />
                        Refresh
                    </Button>
                </div>

                {/* Stats Cards */}
                {stats && (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
                        <StatCard 
                            icon={<CreditCard className="w-5 h-5" />}
                            label="Unpaid Orders"
                            value={stats.unpaid_count ?? 0}
                            subtitle={stats.unpaid_count ? `$${(stats.unpaid_total || 0).toFixed(2)} awaiting` : 'All settled'}
                            color={stats.unpaid_count ? 'amber' : 'green'}
                            onClick={jumpToUnpaid}
                            testid="stat-unpaid"
                        />
                        <StatCard 
                            icon={<Clock className="w-5 h-5" />}
                            label="Today's Orders"
                            value={stats.todays_orders}
                            color="orange"
                            onClick={() => { setStatusFilter('all'); setActiveTab('orders'); }}
                            testid="stat-today"
                        />
                        <StatCard 
                            icon={<Package className="w-5 h-5" />}
                            label="Weekly Orders"
                            value={stats.weekly_orders}
                            color="blue"
                            onClick={() => { setStatusFilter('all'); setActiveTab('orders'); }}
                            testid="stat-weekly"
                        />
                        <StatCard 
                            icon={<Users className="w-5 h-5" />}
                            label="Total Customers"
                            value={stats.total_users}
                            color="green"
                            onClick={() => setActiveTab('contacts')}
                            testid="stat-customers"
                        />
                        <StatCard 
                            icon={<ShoppingCart className="w-5 h-5" />}
                            label="Products Available"
                            value={`${stats.available_products}/${stats.total_products}`}
                            color="purple"
                            onClick={() => setActiveTab('inventory')}
                            testid="stat-products"
                        />
                    </div>
                )}

                {/* Unpaid Orders — chase & resend payment links */}
                <div ref={unpaidRef}>
                    <UnpaidPanel
                        orders={unpaidOrders}
                        now={now}
                        onResend={resendPayment}
                        resendingId={resendingId}
                    />
                </div>

                {/* Order Status Summary */}
                {stats && (
                    <div className="bg-white rounded-xl p-4 mb-8 border border-slate-100">
                        <h3 className="font-semibold mb-3">Orders by Status</h3>
                        <div className="flex flex-wrap gap-3">
                            {SUMMARY_STATUSES.map((status) => (
                                <Badge key={status} className={`${statusColors[status]} px-3 py-1`} data-testid={`status-count-${status}`}>
                                    {status}: {stats.orders_by_status?.[status] || 0}
                                </Badge>
                            ))}
                        </div>
                    </div>
                )}

                {/* Main Tabs */}
                <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
                    <TabsList className="bg-white border border-slate-200 p-1 rounded-lg">
                        <TabsTrigger value="orders" className="gap-2" data-testid="admin-orders-tab">
                            <Package className="w-4 h-4" />
                            Orders ({orders.length})
                        </TabsTrigger>
                        <TabsTrigger value="picking" className="gap-2" data-testid="admin-picking-tab">
                            <ClipboardList className="w-4 h-4" />
                            Picking List
                        </TabsTrigger>
                        <TabsTrigger value="inventory" className="gap-2" data-testid="admin-inventory-tab">
                            <BarChart3 className="w-4 h-4" />
                            Inventory ({products.length})
                        </TabsTrigger>
                        <TabsTrigger value="contacts" className="gap-2" data-testid="admin-contacts-tab">
                            <AlertCircle className="w-4 h-4" />
                            Messages ({contacts.length})
                        </TabsTrigger>
                        <TabsTrigger value="prices" className="gap-2" data-testid="admin-prices-tab">
                            <DollarSign className="w-4 h-4" />
                            Bulk Prices
                        </TabsTrigger>
                    </TabsList>

                    {/* Orders Tab */}
                    <TabsContent value="orders" className="space-y-4">
                        {/* Search + Filter */}
                        <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                            <div className="relative flex-1 max-w-md">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                <Input
                                    placeholder="Search order #, name, phone, email..."
                                    value={orderSearch}
                                    onChange={(e) => setOrderSearch(e.target.value)}
                                    className="pl-9 pr-9"
                                    data-testid="admin-order-search-input"
                                />
                                {orderSearch && (
                                    <button
                                        onClick={() => setOrderSearch('')}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                        data-testid="admin-order-search-clear"
                                        aria-label="Clear search"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                )}
                            </div>
                            <Select value={statusFilter} onValueChange={setStatusFilter}>
                                <SelectTrigger className="w-full sm:w-48" data-testid="admin-order-status-filter">
                                    <SelectValue placeholder="Filter by status" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Orders</SelectItem>
                                    <SelectItem value="pending">Pending</SelectItem>
                                    <SelectItem value="preparing">Preparing</SelectItem>
                                    <SelectItem value="ready">Ready</SelectItem>
                                    <SelectItem value="paid">Paid</SelectItem>
                                    <SelectItem value="dispatched">Dispatched</SelectItem>
                                    <SelectItem value="delivered">Delivered</SelectItem>
                                    <SelectItem value="cancelled">Cancelled</SelectItem>
                                </SelectContent>
                            </Select>
                            <Select value={orderGrouping} onValueChange={(v) => { setOrderGrouping(v); setCollapsedGroups({}); }}>
                                <SelectTrigger className="w-full sm:w-44" data-testid="admin-order-group-select">
                                    <SelectValue placeholder="Group by" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="day">Group by Day</SelectItem>
                                    <SelectItem value="week">Group by Week</SelectItem>
                                    <SelectItem value="month">Group by Month</SelectItem>
                                </SelectContent>
                            </Select>
                            <div className="flex items-center gap-1.5">
                                <input
                                    type="date"
                                    value={dateFrom}
                                    onChange={(e) => setDateFrom(e.target.value)}
                                    className="h-10 rounded-md border border-slate-200 px-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#1B4D3E]/30"
                                    data-testid="admin-order-date-from"
                                    aria-label="From date"
                                />
                                <span className="text-slate-400 text-sm">–</span>
                                <input
                                    type="date"
                                    value={dateTo}
                                    onChange={(e) => setDateTo(e.target.value)}
                                    className="h-10 rounded-md border border-slate-200 px-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#1B4D3E]/30"
                                    data-testid="admin-order-date-to"
                                    aria-label="To date"
                                />
                                {(dateFrom || dateTo) && (
                                    <button
                                        type="button"
                                        onClick={() => { setDateFrom(''); setDateTo(''); }}
                                        className="text-slate-400 hover:text-slate-600 p-1"
                                        data-testid="admin-order-date-clear"
                                        aria-label="Clear date range"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                )}
                            </div>
                            <div className="flex items-center gap-2 ml-auto">
                                <Button variant="ghost" size="sm" className="text-slate-600" onClick={expandAllGroups} data-testid="admin-expand-all-btn">Expand all</Button>
                                <Button variant="ghost" size="sm" className="text-slate-600" onClick={collapseAllGroups} data-testid="admin-collapse-all-btn">Collapse all</Button>
                                <span className="text-sm text-slate-500 whitespace-nowrap" data-testid="admin-orders-count">
                                    Showing {filteredOrders.length} orders
                                </span>
                            </div>
                        </div>

                        {/* Orders List (grouped by placement date) */}
                        {filteredOrders.length === 0 ? (
                            <div className="bg-white rounded-xl p-8 text-center border border-slate-100">
                                <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                                <p className="text-slate-500">{orderSearch ? `No orders match "${orderSearch}"` : 'No orders found'}</p>
                            </div>
                        ) : (
                            <div className="space-y-8">
                                {groupedOrders.map((group) => {
                                    const collapsed = !!collapsedGroups[group.key];
                                    return (
                                        <div key={group.key} data-testid={`order-group-${group.key}`}>
                                            <div
                                                onClick={() => toggleGroup(group.key)}
                                                className="flex items-center gap-3 mb-3 cursor-pointer select-none"
                                                data-testid={`order-group-header-${group.key}`}
                                            >
                                                <h3 className="text-sm sm:text-base font-bold text-slate-800 whitespace-nowrap" data-testid={`order-group-label-${group.key}`}>
                                                    {group.label}
                                                </h3>
                                                <span className="text-xs font-bold text-[#1B4D3E] bg-[#1B4D3E]/10 rounded-full px-2.5 py-0.5 whitespace-nowrap" data-testid={`order-group-count-${group.key}`}>
                                                    {group.orders.length} Order{group.orders.length !== 1 ? 's' : ''}
                                                </span>
                                                <span className="text-xs font-bold text-green-700 whitespace-nowrap" data-testid={`order-group-total-${group.key}`}>
                                                    · ${group.total.toFixed(2)}
                                                </span>
                                                <div className="flex-1 h-px bg-slate-200" />
                                                <ChevronDown
                                                    className={`w-5 h-5 text-slate-500 shrink-0 transition-transform duration-200 ${collapsed ? '-rotate-90' : ''}`}
                                                    data-testid={`order-group-toggle-${group.key}`}
                                                />
                                            </div>
                                            {!collapsed && (
                                                <div className="space-y-4">
                                                    {group.orders.map((order) => (
                                                        <OrderCard 
                                                            key={order.order_id}
                                                            order={order}
                                                            statusColors={statusColors}
                                                            onStatusChange={updateOrderStatus}
                                                            onWeightsSaved={refreshData}
                                                        />
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </TabsContent>

                    {/* Picking List Tab */}
                    <TabsContent value="picking" className="space-y-4">
                        <PickingListSection 
                            pickingList={pickingList}
                            statusFilter={pickingStatusFilter}
                            onStatusFilterChange={(value) => {
                                setPickingStatusFilter(value);
                            }}
                            onRefresh={fetchPickingList}
                        />
                    </TabsContent>

                    {/* Inventory Tab */}
                    <TabsContent value="inventory" className="space-y-4">
                        {/* Filters */}
                        <div className="flex flex-col sm:flex-row gap-4">
                            <div className="relative flex-1">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                <Input
                                    placeholder="Search products..."
                                    value={productSearch}
                                    onChange={(e) => setProductSearch(e.target.value)}
                                    className="pl-10"
                                />
                            </div>
                            <Select value={productCategory} onValueChange={setProductCategory}>
                                <SelectTrigger className="w-48">
                                    <SelectValue placeholder="Category" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Categories</SelectItem>
                                    {categories.map(cat => (
                                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Products Table */}
                        <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-slate-50 border-b">
                                        <tr>
                                            <th className="text-left p-4 font-medium text-slate-600">Product</th>
                                            <th className="text-left p-4 font-medium text-slate-600">Category</th>
                                            <th className="text-left p-4 font-medium text-slate-600">Unit</th>
                                            <th className="text-center p-4 font-medium text-slate-600">Stock Qty</th>
                                            <th className="text-center p-4 font-medium text-slate-600">Available</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredProducts.map((product) => (
                                            <tr key={product.product_id} className="border-b last:border-0 hover:bg-slate-50">
                                                <td className="p-4">
                                                    <div className="flex items-center gap-3">
                                                        <label
                                                            className="relative group w-10 h-10 shrink-0 cursor-pointer"
                                                            title="Upload a photo for this product"
                                                            data-testid={`upload-image-${product.product_id}`}
                                                        >
                                                            <img 
                                                                src={product.image_url} 
                                                                alt={product.name}
                                                                className="w-10 h-10 rounded-lg object-cover"
                                                                onError={(e) => e.target.src = 'https://images.unsplash.com/photo-1540420773420-3366772f4999?w=100'}
                                                            />
                                                            <span className="absolute inset-0 rounded-lg bg-black/0 group-hover:bg-black/40 flex items-center justify-center transition-colors">
                                                                <Camera className="w-4 h-4 text-white opacity-0 group-hover:opacity-100" />
                                                            </span>
                                                            <input
                                                                type="file"
                                                                accept="image/*"
                                                                className="hidden"
                                                                onChange={(e) => {
                                                                    const file = e.target.files?.[0];
                                                                    if (file) uploadProductImage(product.product_id, file);
                                                                    e.target.value = '';
                                                                }}
                                                            />
                                                        </label>
                                                        <span className="font-medium">{product.name}</span>
                                                        {product.image_is_custom && (
                                                            <span className="text-[10px] uppercase tracking-wide text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded">Custom</span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="p-4 text-slate-600">{product.category}</td>
                                                <td className="p-4 text-slate-600">{product.unit}</td>
                                                <td className="p-4">
                                                    <div className="flex justify-center">
                                                        <Input
                                                            type="number"
                                                            min="0"
                                                            value={product.stock_quantity ?? 0}
                                                            onChange={(e) => updateStockQuantity(product.product_id, parseInt(e.target.value) || 0)}
                                                            className="w-20 text-center"
                                                            data-testid={`stock-${product.product_id}`}
                                                        />
                                                    </div>
                                                </td>
                                                <td className="p-4">
                                                    <div className="flex justify-center">
                                                        <Switch
                                                            checked={product.available}
                                                            onCheckedChange={(checked) => 
                                                                toggleProductAvailability(product.product_id, checked)
                                                            }
                                                            data-testid={`toggle-${product.product_id}`}
                                                        />
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </TabsContent>

                    {/* Contacts Tab */}
                    <TabsContent value="contacts" className="space-y-4">
                        {contacts.length === 0 ? (
                            <div className="bg-white rounded-xl p-8 text-center border border-slate-100">
                                <AlertCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                                <p className="text-slate-500">No messages yet</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {contacts.map((contact) => (
                                    <div 
                                        key={contact.contact_id}
                                        className="bg-white rounded-xl p-4 border border-slate-100"
                                    >
                                        <div className="flex justify-between items-start mb-2">
                                            <div>
                                                <h4 className="font-semibold">{contact.name}</h4>
                                                <p className="text-sm text-slate-500">{contact.email}</p>
                                                {contact.phone && (
                                                    <p className="text-sm text-slate-500">{contact.phone}</p>
                                                )}
                                            </div>
                                            <span className="text-xs text-slate-400">
                                                {new Date(contact.created_at).toLocaleDateString()}
                                            </span>
                                        </div>
                                        <p className="text-slate-700 bg-slate-50 p-3 rounded-lg">
                                            {contact.message}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        )}
                    </TabsContent>

                    {/* Bulk Prices Tab */}
                    <TabsContent value="prices" className="space-y-4">
                        <BulkPriceUpdate />
                    </TabsContent>
                </Tabs>
            </div>
        </div>
    );
}

const StatCard = ({ icon, label, value, color, subtitle, onClick, testid }) => {
    const colorClasses = {
        orange: 'bg-orange-100 text-orange-600',
        blue: 'bg-blue-100 text-blue-600',
        green: 'bg-green-100 text-green-600',
        purple: 'bg-purple-100 text-purple-600',
        amber: 'bg-amber-100 text-amber-600'
    };
    const clickable = typeof onClick === 'function';
    return (
        <div
            onClick={onClick}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            onKeyDown={clickable ? (e) => { if (e.key === 'Enter') onClick(); } : undefined}
            data-testid={testid}
            className={`bg-white rounded-xl p-4 border transition-all duration-200 ${
                color === 'amber' ? 'border-amber-200' : 'border-slate-100'
            } ${clickable ? 'cursor-pointer hover:shadow-md hover:-translate-y-0.5 hover:border-slate-300' : ''}`}
        >
            <div className="flex items-start justify-between">
                <div className={`w-10 h-10 rounded-lg ${colorClasses[color]} flex items-center justify-center mb-3`}>
                    {icon}
                </div>
                {clickable && <ChevronRight className="w-4 h-4 text-slate-300 mt-1" />}
            </div>
            <p className="text-2xl font-bold text-slate-900">{value}</p>
            <p className="text-sm text-slate-500">{label}</p>
            {subtitle && (
                <p className={`text-xs mt-1 font-medium ${color === 'amber' ? 'text-amber-600' : 'text-slate-400'}`}>{subtitle}</p>
            )}
        </div>
    );
};

// "2d 3h", "4h 10m", "25m" — compact elapsed/remaining formatter from milliseconds.
const fmtDuration = (ms) => {
    if (ms == null || isNaN(ms)) return '—';
    const mins = Math.floor(Math.abs(ms) / 60000);
    const d = Math.floor(mins / 1440);
    const h = Math.floor((mins % 1440) / 60);
    const m = mins % 60;
    const parts = [];
    if (d) parts.push(`${d}d`);
    if (h) parts.push(`${h}h`);
    if (!d) parts.push(`${m}m`);
    return parts.join(' ') || '0m';
};

const UnpaidPanel = ({ orders, now, onResend, resendingId }) => {
    const fmtMoney = (n) => `$${(Math.round(((Number(n) || 0) + Number.EPSILON) * 100) / 100).toFixed(2)}`;
    if (!orders.length) {
        return (
            <div className="bg-white rounded-xl p-5 mb-8 border border-slate-100 flex items-center gap-3" data-testid="unpaid-panel-empty">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <p className="text-sm text-slate-600">All caught up — no customers are currently awaiting payment.</p>
            </div>
        );
    }
    return (
        <div className="bg-white rounded-xl mb-8 border border-amber-200 overflow-hidden" data-testid="unpaid-panel">
            <div className="flex items-center justify-between gap-3 px-5 py-4 bg-amber-50 border-b border-amber-200">
                <div className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-amber-600" />
                    <h3 className="font-semibold text-amber-900">Awaiting Payment ({orders.length})</h3>
                </div>
                <p className="text-sm text-amber-700">Sorted by longest unpaid · resend if the link has expired</p>
            </div>
            <div className="divide-y divide-slate-100">
                {orders.map((o) => {
                    const amount = o.actual_total != null ? o.actual_total : (o.total != null ? o.total : 0);
                    const sentAt = o.payment_link_sent_at || o.updated_at || o.created_at;
                    const unpaidForMs = sentAt ? now - new Date(sentAt).getTime() : null;
                    const expiresAt = o.payment_expires_at;
                    const remainingMs = expiresAt ? new Date(expiresAt).getTime() - now : null;
                    const expired = remainingMs != null && remainingMs <= 0;
                    const customer = o.business_name || o.user_name || 'Customer';
                    const orderNo = (o.order_id || '').slice(-8);
                    return (
                        <div key={o.order_id} className="px-5 py-4 flex flex-col md:flex-row md:items-center gap-3 md:gap-4" data-testid={`unpaid-row-${orderNo}`}>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-semibold text-slate-900 truncate">{customer}</span>
                                    <span className="text-xs text-slate-400">#{orderNo}</span>
                                </div>
                                <div className="text-sm text-slate-500 mt-0.5">
                                    {o.phone || o.user_email || '—'}
                                </div>
                            </div>
                            <div className="text-right md:text-left md:w-28">
                                <p className="text-lg font-bold text-slate-900">{fmtMoney(amount)}</p>
                            </div>
                            <div className="md:w-44">
                                <div className="flex items-center gap-1.5 text-sm text-slate-600">
                                    <Timer className="w-4 h-4 text-slate-400" />
                                    Unpaid for <strong className="text-slate-800">{fmtDuration(unpaidForMs)}</strong>
                                </div>
                                {expiresAt && (
                                    expired ? (
                                        <div className="flex items-center gap-1.5 text-xs text-red-600 font-medium mt-1" data-testid={`unpaid-expired-${orderNo}`}>
                                            <AlertTriangle className="w-3.5 h-3.5" /> Link expired
                                        </div>
                                    ) : (
                                        <div className="text-xs text-amber-600 mt-1">Link expires in {fmtDuration(remainingMs)}</div>
                                    )
                                )}
                            </div>
                            <div className="md:w-40 flex md:justify-end gap-2">
                                {o.payment_url && (
                                    <Button
                                        variant="outline" size="sm"
                                        onClick={() => window.open(o.payment_url, '_blank')}
                                        data-testid={`copy-link-${orderNo}`}
                                        title="Open the current payment link"
                                    >
                                        <DollarSign className="w-4 h-4" />
                                    </Button>
                                )}
                                <Button
                                    size="sm"
                                    className="gap-2 bg-[#1B4D3E] hover:bg-[#143d31]"
                                    disabled={resendingId === o.order_id}
                                    onClick={() => onResend(o.order_id)}
                                    data-testid={`resend-payment-${orderNo}`}
                                >
                                    {resendingId === o.order_id
                                        ? <Loader2 className="w-4 h-4 animate-spin" />
                                        : <Send className="w-4 h-4" />}
                                    Resend
                                </Button>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// Commercial money rounding (half-up) computed in integer units to avoid float drift
// and to exactly match the backend Decimal(ROUND_HALF_UP) calculation.
const lineAmount = (price, factor) => {
    const p = Math.round((Number(price) || 0) * 100);      // price in cents (2 dp)
    const f = Math.round((Number(factor) || 0) * 1000);    // factor in milli (3 dp)
    const scaled = p * f;                                   // = amount * 100000
    const cents = Math.floor(scaled / 1000) + ((Math.abs(scaled) % 1000) >= 500 ? Math.sign(scaled) || 1 : 0);
    return cents / 100;
};
const round2 = (n) => Math.round(((Number(n) || 0) + Number.EPSILON) * 100) / 100;
const money = (n) => `$${(Math.round(((Number(n) || 0) + Number.EPSILON) * 100) / 100).toFixed(2)}`;

const OrderCard = ({ order, statusColors, onStatusChange, onWeightsSaved }) => {
    const [expanded, setExpanded] = useState(false);
    const [saving, setSaving] = useState(false);
    const [pendingTransition, setPendingTransition] = useState(null);
    const [transitioning, setTransitioning] = useState(false);
    const items = order.items || [];

    const [weights, setWeights] = useState(() =>
        items.map(it => (it.actual_weight != null ? String(it.actual_weight) : String(it.quantity ?? '')))
    );

    useEffect(() => {
        setWeights(items.map(it => (it.actual_weight != null ? String(it.actual_weight) : String(it.quantity ?? ''))));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [order.items]);

    const deliveryFee = order.delivery_type === 'delivery' ? (order.delivery_fee ?? 15) : 0;
    const itemsEstimated = round2(items.reduce((s, it) => s + lineAmount(it.price, it.quantity), 0));
    const itemsActual = round2(items.reduce((s, it, i) => s + lineAmount(it.price, parseFloat(weights[i]) || 0), 0));
    const estimatedTotal = round2(itemsEstimated + deliveryFee);
    const actualTotal = round2(itemsActual + deliveryFee);
    const displayTotal = order.actual_total != null ? order.actual_total : (order.total != null ? order.total : estimatedTotal);
    const totalDiff = round2(actualTotal - estimatedTotal);

    const canEditWeights = order.status === 'confirmed' || order.status === 'preparing';
    const isDelivery = order.delivery_type === 'delivery';
    const di = order.delivery_info || {};

    const handleWeightChange = (i, val) => {
        setWeights(prev => { const next = [...prev]; next[i] = val; return next; });
    };

    const handleSaveWeights = async () => {
        setSaving(true);
        try {
            await axios.put(
                `${API}/admin/orders/${order.order_id}/weights`,
                { weights: weights.map(w => (w === '' || w == null ? null : parseFloat(w))) },
                { withCredentials: true }
            );
            toast.success('Weights confirmed and order total updated');
            if (onWeightsSaved) onWeightsSaved();
        } catch (e) {
            toast.error('Failed to save weights');
        } finally {
            setSaving(false);
        }
    };

    const orderNo = order.order_id.slice(-8);
    const TRANSITIONS = {
        pending: [{ value: 'preparing', label: 'Confirm & Prepare' }, { value: 'cancelled', label: 'Cancel Order' }],
        preparing: [{ value: 'ready', label: 'Mark as Ready' }, { value: 'cancelled', label: 'Cancel Order' }],
        ready: [{ value: 'cancelled', label: 'Cancel Order' }],
        paid: [{ value: 'dispatched', label: 'Mark as Dispatched' }],
        dispatched: [{ value: 'delivered', label: 'Mark as Delivered' }],
        delivered: [],
        cancelled: [],
        confirmed: [{ value: 'ready', label: 'Mark as Ready' }, { value: 'cancelled', label: 'Cancel Order' }],
    };
    const allowedTransitions = TRANSITIONS[order.status] || [];
    const weightsMissing = items.length === 0 || items.some(it => it.actual_weight == null);
    const readyTotal = order.actual_total != null ? order.actual_total : actualTotal;

    const modalConfig = (target) => {
        switch (target) {
            case 'preparing':
                return {
                    title: 'Confirm Order?',
                    message: `Are you sure you want to confirm Order #${orderNo}? The order will be automatically moved to Preparing and the customer will be notified that their order is confirmed and being prepared.`,
                    confirmLabel: 'Yes, Confirm Order',
                    confirmClass: 'bg-[#1B4D3E] hover:bg-[#143a2f] text-white',
                    cancelLabel: 'Cancel',
                };
            case 'ready':
                return {
                    title: 'Mark Order as Ready & Send Payment?',
                    message: `You are about to mark Order #${orderNo} as Ready. The final order total of ${money(readyTotal)} will be used to generate a Stripe Payment Link, which will be automatically emailed to the customer at ${order.user_email || 'their email'}. Do you want to proceed?`,
                    confirmLabel: 'Yes, Send Payment Link',
                    confirmClass: 'bg-[#1B4D3E] hover:bg-[#143a2f] text-white',
                    cancelLabel: 'Cancel',
                    warning: weightsMissing ? '\u26A0\uFE0F Some items are missing actual weights. Please enter all weights before marking as Ready.' : null,
                    disableConfirm: weightsMissing,
                };
            case 'dispatched':
                return {
                    title: 'Mark as Dispatched?',
                    message: `Are you sure you want to mark Order #${orderNo} as Dispatched? This means the order has left for delivery or is ready for customer pickup.`,
                    confirmLabel: 'Yes, Mark Dispatched',
                    confirmClass: 'bg-blue-600 hover:bg-blue-700 text-white',
                    cancelLabel: 'Cancel',
                };
            case 'delivered':
                return {
                    title: 'Mark as Delivered?',
                    message: `Are you sure you want to mark Order #${orderNo} as Delivered? This confirms the customer has received their order.`,
                    confirmLabel: 'Yes, Mark Delivered',
                    confirmClass: 'bg-[#1B4D3E] hover:bg-[#143a2f] text-white',
                    cancelLabel: 'Cancel',
                };
            case 'cancelled':
                return {
                    title: 'Cancel This Order?',
                    message: `Are you sure you want to cancel Order #${orderNo}? This action cannot be undone. If the customer has already paid, you will need to process a refund manually via Stripe.`,
                    confirmLabel: 'Yes, Cancel Order',
                    confirmClass: 'bg-red-600 hover:bg-red-700 text-white',
                    cancelLabel: 'Go Back',
                };
            default:
                return null;
        }
    };
    const cfg = pendingTransition ? modalConfig(pendingTransition) : null;

    const handleConfirmTransition = async () => {
        const target = pendingTransition;
        setTransitioning(true);
        try {
            await axios.put(
                `${API}/admin/orders/${order.order_id}/status`,
                { status: target, origin_url: window.location.origin },
                { withCredentials: true }
            );
            const messages = {
                preparing: `Order #${orderNo} confirmed and moved to Preparing`,
                ready: `Order #${orderNo} marked as Ready \u2014 payment link emailed`,
                dispatched: `Order #${orderNo} marked as Dispatched`,
                delivered: `Order #${orderNo} marked as Delivered`,
                cancelled: `Order #${orderNo} cancelled`,
            };
            toast.success(messages[target] || 'Order updated', { duration: 3000 });
            setPendingTransition(null);
            if (onWeightsSaved) onWeightsSaved();
        } catch (e) {
            const msg = e.response?.data?.detail || 'Update failed. Please try again.';
            toast.error(msg, { duration: 3000 });
        } finally {
            setTransitioning(false);
        }
    };

    return (
        <div className={`bg-white rounded-xl border overflow-hidden ${isDelivery ? 'border-slate-100' : 'border-slate-100 border-l-4 border-l-amber-400'}`} data-testid={`order-card-${order.order_id}`}>
            <div 
                className="p-4 cursor-pointer hover:bg-slate-50"
                onClick={() => setExpanded(!expanded)}
            >
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                    <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                            <h4 className="font-semibold">
                                Order #{order.order_id.slice(-8)}
                            </h4>
                            <Badge className={statusColors[order.status]}>
                                {order.status}
                            </Badge>
                            <Badge
                                variant={isDelivery ? 'outline' : undefined}
                                className={isDelivery ? 'gap-1' : 'gap-1 bg-amber-100 text-amber-800 border border-amber-300'}
                                data-testid={`order-type-${order.order_id}`}
                            >
                                {isDelivery ? <Truck className="w-3 h-3" /> : <MapPin className="w-3 h-3" />}
                                {isDelivery ? 'Delivery' : 'PICKUP'}
                            </Badge>
                        </div>
                        <div className="text-sm text-slate-500 space-y-1">
                            <p>
                                <span className="font-medium">{order.user_name || 'Guest'}</span>
                                {order.business_name && ` (${order.business_name})`}
                            </p>
                            {(order.user_email || order.phone || di.phone) && (
                                <p className="text-slate-500" data-testid={`order-contact-${order.order_id}`}>
                                    {order.user_email}
                                    {(order.phone || di.phone) && `${order.user_email ? ' • ' : ''}${order.phone || di.phone}`}
                                </p>
                            )}
                            <p>{items.length} items • {new Date(order.created_at).toLocaleString()}</p>
                            {/* Delivery address / Pickup details below the delivery badge */}
                            {isDelivery ? (
                                <>
                                    {di.address || di.suburb ? (
                                        <p className="flex items-start gap-1 text-slate-600" data-testid={`order-address-${order.order_id}`}>
                                            <MapPin className="w-3.5 h-3.5 mt-0.5 text-[#1B4D3E]" />
                                            <span>
                                                {di.suburb && <span className="font-medium">{di.suburb}</span>}
                                                {di.address && <span> — {di.address}</span>}
                                                {di.postcode && <span> {di.postcode}</span>}
                                            </span>
                                        </p>
                                    ) : (
                                        <p className="text-slate-400 italic">No delivery address provided</p>
                                    )}
                                    {(di.fulfil_label || di.delivery_date) && (
                                        <p className="flex items-center gap-1 text-slate-600" data-testid={`order-schedule-${order.order_id}`}>
                                            <Clock className="w-3.5 h-3.5 text-[#1B4D3E]" />
                                            <span>Delivery: {di.fulfil_label || di.delivery_date}{di.time_slot_label ? ` · ${di.time_slot_label}` : ''}</span>
                                        </p>
                                    )}
                                </>
                            ) : (
                                <>
                                    <p className="flex items-center gap-1 text-amber-700 font-medium" data-testid={`order-pickup-${order.order_id}`}>
                                        <Clock className="w-3.5 h-3.5" />
                                        <span>Pickup: {di.fulfil_label || di.delivery_date || 'Not specified'}{di.window_label ? ` · ${di.window_label}` : ''}</span>
                                    </p>
                                    <p className="flex items-start gap-1 text-amber-700">
                                        <MapPin className="w-3.5 h-3.5 mt-0.5" />
                                        <span>{(di.pickup_location && (di.pickup_location.name + ' — ' + di.pickup_location.address)) || 'Orange Grove Market, Lilyfield NSW 2040'}</span>
                                    </p>
                                </>
                            )}
                        </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                        {/* Order total - prominent green, above status dropdown */}
                        <div className="text-right" data-testid={`order-total-${order.order_id}`}>
                            <p className="text-xl font-bold text-green-600">{money(displayTotal)}</p>
                            {order.actual_total != null && (
                                <p className="text-[11px] text-slate-400">actual weighed total</p>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            {allowedTransitions.length > 0 ? (
                                <Select
                                    value=""
                                    onValueChange={(value) => setPendingTransition(value)}
                                >
                                    <SelectTrigger
                                        className="w-44"
                                        onClick={(e) => e.stopPropagation()}
                                        data-testid={`status-select-${order.order_id}`}
                                    >
                                        <SelectValue placeholder="Change status ▾" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {allowedTransitions.map((t) => (
                                            <SelectItem
                                                key={t.value}
                                                value={t.value}
                                                data-testid={`status-option-${t.value}-${order.order_id}`}
                                            >
                                                {t.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            ) : (
                                <span className="text-xs text-slate-400 w-44 text-right">No further actions</span>
                            )}
                            <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
                        </div>
                    </div>
                </div>
            </div>
            
            {expanded && (
                <div className="border-t p-4 bg-slate-50">
                    <h5 className="font-medium mb-3 flex items-center gap-2">
                        <Scale className="w-4 h-4 text-[#1B4D3E]" />
                        Order Items & Weights
                    </h5>
                    <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-100 text-slate-600">
                                <tr>
                                    <th className="text-left font-medium px-3 py-2">Product Name</th>
                                    <th className="text-right font-medium px-3 py-2">Unit Price ($)</th>
                                    <th className="text-right font-medium px-3 py-2">Est. Weight / Qty</th>
                                    <th className="text-right font-medium px-3 py-2">Actual Weight (kg)</th>
                                    <th className="text-right font-medium px-3 py-2">Final Amount ($)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((item, idx) => {
                                    const lineFinal = lineAmount(item.price, parseFloat(weights[idx]) || 0);
                                    return (
                                        <tr key={idx} className="border-t border-slate-100">
                                            <td className="px-3 py-2 text-slate-800">{item.name}</td>
                                            <td className="px-3 py-2 text-right text-slate-600">
                                                {money(item.price)} <span className="text-slate-400">/{item.unit}</span>
                                            </td>
                                            <td className="px-3 py-2 text-right text-slate-600">
                                                {item.quantity} {item.unit}
                                            </td>
                                            <td className="px-3 py-2 text-right">
                                                <Input
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    value={weights[idx]}
                                                    disabled={!canEditWeights}
                                                    onChange={(e) => handleWeightChange(idx, e.target.value)}
                                                    className="w-24 h-8 text-right ml-auto"
                                                    data-testid={`actual-weight-input-${order.order_id}-${idx}`}
                                                />
                                            </td>
                                            <td className="px-3 py-2 text-right font-bold text-green-600" data-testid={`final-amount-${order.order_id}-${idx}`}>
                                                {money(lineFinal)}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Estimated vs Actual totals side by side */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
                        <div className="bg-white rounded-lg border border-slate-200 p-3">
                            <p className="text-xs text-slate-500">Estimated Total</p>
                            <p className="text-lg font-semibold text-slate-700" data-testid={`estimated-total-${order.order_id}`}>{money(estimatedTotal)}</p>
                        </div>
                        <div className="bg-white rounded-lg border border-green-200 p-3">
                            <p className="text-xs text-slate-500">Actual (Weighed) Total</p>
                            <p className="text-lg font-bold text-green-600" data-testid={`actual-total-${order.order_id}`}>{money(actualTotal)}</p>
                        </div>
                        <div className="bg-white rounded-lg border border-slate-200 p-3">
                            <p className="text-xs text-slate-500">Difference</p>
                            <p className={`text-lg font-semibold ${totalDiff > 0 ? 'text-orange-600' : totalDiff < 0 ? 'text-blue-600' : 'text-slate-700'}`}>
                                {totalDiff > 0 ? '+' : ''}{money(totalDiff)}
                            </p>
                        </div>
                    </div>

                    {deliveryFee > 0 && (
                        <p className="text-xs text-slate-500 mt-2" data-testid={`admin-delivery-fee-${order.order_id}`}>
                            Totals include a flat delivery fee of {money(deliveryFee)} (items only: est {money(itemsEstimated)} / weighed {money(itemsActual)}).
                        </p>
                    )}

                    {/* Confirm Weights & Save - only for confirmed or preparing */}
                    {canEditWeights && (
                        <div className="mt-4 flex justify-end">
                            <Button
                                onClick={handleSaveWeights}
                                disabled={saving}
                                className="bg-[#1B4D3E] hover:bg-[#143a2f] gap-2"
                                data-testid={`confirm-weights-btn-${order.order_id}`}
                            >
                                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                Confirm Weights &amp; Save
                            </Button>
                        </div>
                    )}

                    {order.delivery_info && (
                        <div className="mt-4 pt-4 border-t">
                            <h5 className="font-medium mb-2">{isDelivery ? 'Delivery Info' : 'Pickup Info'}:</h5>
                            {isDelivery ? (
                                <p className="text-sm text-slate-600">
                                    {di.address}, {di.suburb} {di.postcode}
                                </p>
                            ) : (
                                <p className="text-sm text-slate-600">
                                    Pickup date/time: {di.delivery_date || 'Not specified'}
                                </p>
                            )}
                            {di.notes && (
                                <p className="text-sm text-slate-500 mt-1">
                                    Notes: {di.notes}
                                </p>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Status change confirmation modal */}
            <Dialog open={!!pendingTransition} onOpenChange={(open) => { if (!open) setPendingTransition(null); }}>
                <DialogContent className="sm:max-w-md" data-testid={`status-modal-${order.order_id}`}>
                    <DialogHeader>
                        <p className="font-bold text-base text-slate-900" data-testid="status-modal-order-no">Order #{orderNo}</p>
                        <DialogTitle className="text-lg">{cfg?.title}</DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-slate-600">{cfg?.message}</p>
                    {cfg?.warning && (
                        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3" data-testid="status-modal-warning">
                            {cfg.warning}
                        </div>
                    )}
                    <DialogFooter className="gap-2 sm:gap-2">
                        <Button
                            variant="outline"
                            onClick={() => setPendingTransition(null)}
                            className="bg-slate-100 text-slate-700 hover:bg-slate-200 border-slate-200"
                            data-testid="status-modal-cancel-btn"
                        >
                            {cfg?.cancelLabel}
                        </Button>
                        <Button
                            onClick={handleConfirmTransition}
                            disabled={transitioning || cfg?.disableConfirm}
                            className={cfg?.confirmClass}
                            data-testid="status-modal-confirm-btn"
                        >
                            {transitioning && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                            {cfg?.confirmLabel}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};
const PickingListSection = ({ pickingList, statusFilter, onStatusFilterChange, onRefresh }) => {
    const [pickedItems, setPickedItems] = useState({});
    const [expandedCategories, setExpandedCategories] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [showPickedOnly, setShowPickedOnly] = useState(false);

    useEffect(() => {
        onRefresh();
    }, [statusFilter]);

    const togglePicked = (productId) => {
        setPickedItems(prev => ({
            ...prev,
            [productId]: !prev[productId]
        }));
    };

    const toggleCategory = (category) => {
        setExpandedCategories(prev => ({
            ...prev,
            [category]: !prev[category]
        }));
    };

    const handlePrint = () => {
        window.print();
    };

    const handleExportCSV = () => {
        if (!pickingList?.picking_list) return;
        
        const headers = ['Product', 'Category', 'Total Quantity', 'Unit', 'Order Count'];
        const rows = pickingList.picking_list.map(item => [
            item.name,
            item.category,
            item.total_quantity,
            item.unit,
            item.order_count
        ]);
        
        const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `picking-list-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success('Picking list exported');
    };

    if (!pickingList) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
        );
    }

    // Group by category
    const categorizedItems = {};
    pickingList.picking_list?.forEach(item => {
        if (!categorizedItems[item.category]) {
            categorizedItems[item.category] = [];
        }
        categorizedItems[item.category].push(item);
    });

    // Filter items
    const filteredCategories = Object.entries(categorizedItems).reduce((acc, [category, items]) => {
        const filtered = items.filter(item => {
            const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesPicked = !showPickedOnly || pickedItems[item.product_id];
            return matchesSearch && matchesPicked;
        });
        if (filtered.length > 0) {
            acc[category] = filtered;
        }
        return acc;
    }, {});

    const pickedCount = Object.values(pickedItems).filter(Boolean).length;
    const totalItems = pickingList.picking_list?.length || 0;

    return (
        <div className="space-y-6">
            {/* Header & Controls */}
            <div className="bg-white rounded-xl p-6 border border-slate-100">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
                    <div>
                        <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                            <ClipboardList className="w-6 h-6 text-orange-500" />
                            Consolidated Picking List
                        </h2>
                        <p className="text-slate-500 text-sm mt-1">
                            Aggregate all items from orders for bulk picking
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Button variant="outline" size="sm" onClick={onRefresh} className="gap-2">
                            <RefreshCw className="w-4 h-4" />
                            Refresh
                        </Button>
                        <Button variant="outline" size="sm" onClick={handlePrint} className="gap-2">
                            <Printer className="w-4 h-4" />
                            Print
                        </Button>
                        <Button variant="outline" size="sm" onClick={handleExportCSV} className="gap-2">
                            <Download className="w-4 h-4" />
                            Export CSV
                        </Button>
                    </div>
                </div>

                {/* Filters */}
                <div className="flex flex-col sm:flex-row gap-4">
                    <Select value={statusFilter} onValueChange={onStatusFilterChange}>
                        <SelectTrigger className="w-full sm:w-64">
                            <SelectValue placeholder="Filter by order status" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="pending">Pending Only</SelectItem>
                            <SelectItem value="confirmed">Confirmed Only</SelectItem>
                            <SelectItem value="preparing">Preparing Only</SelectItem>
                            <SelectItem value="pending,confirmed">Pending + Confirmed</SelectItem>
                            <SelectItem value="pending,confirmed,preparing">All Active Orders</SelectItem>
                        </SelectContent>
                    </Select>
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                            placeholder="Search products..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10"
                        />
                    </div>
                </div>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
                    <p className="text-blue-600 text-sm font-medium">Total Orders</p>
                    <p className="text-2xl font-bold text-blue-900">{pickingList.summary?.total_orders || 0}</p>
                </div>
                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 border border-green-200">
                    <p className="text-green-600 text-sm font-medium">Unique Products</p>
                    <p className="text-2xl font-bold text-green-900">{pickingList.summary?.total_unique_products || 0}</p>
                </div>
                <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-4 border border-orange-200">
                    <p className="text-orange-600 text-sm font-medium">Categories</p>
                    <p className="text-2xl font-bold text-orange-900">{Object.keys(categorizedItems).length}</p>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border border-purple-200">
                    <p className="text-purple-600 text-sm font-medium">Picked Progress</p>
                    <p className="text-2xl font-bold text-purple-900">{pickedCount}/{totalItems}</p>
                </div>
            </div>

            {/* Picking List by Category */}
            <div className="space-y-4">
                {Object.entries(filteredCategories).map(([category, items]) => (
                    <div key={category} className="bg-white rounded-xl border border-slate-100 overflow-hidden">
                        <button
                            onClick={() => toggleCategory(category)}
                            className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
                        >
                            <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                    category === 'Vegetables' ? 'bg-green-100 text-green-600' :
                                    category === 'Fruit' ? 'bg-orange-100 text-orange-600' :
                                    category === 'Herbs' ? 'bg-emerald-100 text-emerald-600' :
                                    'bg-amber-100 text-amber-600'
                                }`}>
                                    <Package className="w-5 h-5" />
                                </div>
                                <div className="text-left">
                                    <h3 className="font-semibold text-slate-900">{category}</h3>
                                    <p className="text-sm text-slate-500">{items.length} products</p>
                                </div>
                            </div>
                            <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${
                                expandedCategories[category] ? 'rotate-90' : ''
                            }`} />
                        </button>

                        {expandedCategories[category] && (
                            <div className="border-t">
                                <table className="w-full">
                                    <thead className="bg-slate-50">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Picked</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Product</th>
                                            <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Total Qty</th>
                                            <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Unit</th>
                                            <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Orders</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {items.map((item) => (
                                            <tr 
                                                key={item.product_id}
                                                className={`hover:bg-slate-50 transition-colors ${
                                                    pickedItems[item.product_id] ? 'bg-green-50' : ''
                                                }`}
                                            >
                                                <td className="px-4 py-3">
                                                    <Checkbox
                                                        checked={pickedItems[item.product_id] || false}
                                                        onCheckedChange={() => togglePicked(item.product_id)}
                                                    />
                                                </td>
                                                <td className="px-4 py-3">
                                                    <span className={`font-medium ${
                                                        pickedItems[item.product_id] ? 'line-through text-slate-400' : 'text-slate-900'
                                                    }`}>
                                                        {item.name}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-right">
                                                    <span className={`text-lg font-bold ${
                                                        pickedItems[item.product_id] ? 'text-green-600' : 'text-orange-600'
                                                    }`}>
                                                        {item.total_quantity}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-center">
                                                    <Badge variant="outline" className="text-xs">
                                                        {item.unit}
                                                    </Badge>
                                                </td>
                                                <td className="px-4 py-3 text-center">
                                                    <span className="text-sm text-slate-500">
                                                        {item.order_count} order{item.order_count !== 1 ? 's' : ''}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Empty State */}
            {Object.keys(filteredCategories).length === 0 && (
                <div className="bg-white rounded-xl p-12 text-center border border-slate-100">
                    <ClipboardList className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-700 mb-2">No items to pick</h3>
                    <p className="text-slate-500">
                        {searchTerm ? 'No products match your search' : 'No pending orders found'}
                    </p>
                </div>
            )}
        </div>
    );
};


// ===================== BULK PRICE UPDATE =====================
const BulkPriceUpdate = () => {
    const [preview, setPreview] = useState(null); // { rows, summary, fileName }
    const [uploading, setUploading] = useState(false);
    const [applying, setApplying] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const [history, setHistory] = useState([]);
    const [lastResult, setLastResult] = useState(null); // { updated_count, timestamp }
    const fileInputRef = useRef(null);

    const loadHistory = async () => {
        try {
            const res = await axios.get(`${API}/admin/prices/history`, { withCredentials: true });
            setHistory(res.data || []);
        } catch (e) { /* ignore */ }
    };
    useEffect(() => { loadHistory(); }, []);

    const csvEscape = (v) => {
        const s = String(v ?? '');
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };

    const downloadCsv = (filename, csv) => {
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = filename;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const handleDownloadCurrent = async () => {
        try {
            const res = await axios.get(`${API}/admin/prices/export`, { withCredentials: true, responseType: 'blob' });
            const url = URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
            const a = document.createElement('a');
            a.href = url; a.download = 'current-price-list.xlsx';
            document.body.appendChild(a); a.click(); document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error('Could not download the price list');
        }
    };

    const handleDownloadTemplate = () => {
        const rows = [
            'Category,Product Name,Price,Unit',
            'FRUITS,,,',
            'Fruits,Seville Oranges,8.99,per KG',
            'FRUITS – APPLES,,,',
            'Fruits – Apples,Royal Gala,6.99,per KG',
        ];
        downloadCsv('price-update-template.csv', rows.join('\n'));
    };

    const uploadFile = async (file) => {
        if (!file) return;
        const nameLower = file.name.toLowerCase();
        if (!nameLower.endsWith('.csv') && !nameLower.endsWith('.xlsx')) {
            toast.error('Only .csv and .xlsx files are accepted');
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const res = await axios.post(`${API}/admin/prices/preview`, fd, { withCredentials: true });
            setPreview({ ...res.data, fileName: file.name });
            setLastResult(null);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not read the file');
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragActive(false);
        uploadFile(e.dataTransfer.files?.[0]);
    };

    const handleConfirm = async () => {
        const changes = (preview?.rows || [])
            .filter(r => r.status === 'increase' || r.status === 'decrease')
            .map(r => ({ product_id: r.product_id, new_price: r.new_price }));
        if (changes.length === 0) { toast.error('No price changes to apply'); return; }
        setApplying(true);
        try {
            const res = await axios.post(`${API}/admin/prices/apply`, { changes }, { withCredentials: true });
            setLastResult(res.data);
            toast.success(`${res.data.updated_count} product prices updated successfully`);
            setPreview(null);
            loadHistory();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not update prices');
        } finally {
            setApplying(false);
        }
    };

    const money = (v) => (v == null ? '—' : `$${Number(v).toFixed(2)}`);

    const ChangeIndicator = ({ status }) => {
        if (status === 'increase') return <span className="inline-flex items-center gap-1 text-green-600 font-medium"><ArrowUp className="w-4 h-4" /> Increase</span>;
        if (status === 'decrease') return <span className="inline-flex items-center gap-1 text-red-600 font-medium"><ArrowDown className="w-4 h-4" /> Decrease</span>;
        if (status === 'unchanged') return <span className="inline-flex items-center gap-1 text-slate-400"><Minus className="w-4 h-4" /> No change</span>;
        return <span className="inline-flex items-center gap-1 text-red-600 font-medium"><X className="w-4 h-4" /> Not found</span>;
    };

    return (
        <div className="space-y-6" data-testid="bulk-price-update">
            {!preview ? (
                /* ---------------- Upload screen ---------------- */
                <div className="bg-white rounded-xl p-6 border border-slate-100 space-y-6">
                    <div>
                        <h3 className="text-lg font-bold text-slate-900">Bulk Price Update</h3>
                        <p className="text-sm text-slate-500">Download the current price list, edit the <strong>Price</strong> column, then upload the file to update prices in bulk. Products are matched by exact name; category banner rows are ignored.</p>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3">
                        <Button variant="outline" className="gap-2" onClick={handleDownloadCurrent} data-testid="download-current-prices-btn">
                            <FileDown className="w-4 h-4" />
                            Download Current Price List
                        </Button>
                        <Button variant="outline" className="gap-2" onClick={handleDownloadTemplate} data-testid="download-template-btn">
                            <Download className="w-4 h-4" />
                            Download CSV Template
                        </Button>
                    </div>

                    <div
                        onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                        onDragLeave={(e) => { e.preventDefault(); setDragActive(false); }}
                        onDrop={onDrop}
                        onClick={() => fileInputRef.current?.click()}
                        className={`rounded-xl border-2 border-dashed p-10 text-center cursor-pointer transition-colors ${dragActive ? 'border-[#1B4D3E] bg-[#1B4D3E]/5' : 'border-slate-300 hover:border-[#1B4D3E]'}`}
                        data-testid="bulk-price-dropzone"
                    >
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".csv,.xlsx"
                            className="hidden"
                            onChange={(e) => uploadFile(e.target.files?.[0])}
                            data-testid="bulk-price-file-input"
                        />
                        {uploading ? (
                            <div className="flex flex-col items-center gap-2 text-slate-500">
                                <Loader2 className="w-8 h-8 animate-spin text-[#1B4D3E]" />
                                <p>Reading your file…</p>
                            </div>
                        ) : (
                            <div className="flex flex-col items-center gap-2">
                                <UploadCloud className="w-10 h-10 text-[#1B4D3E]" />
                                <p className="font-medium text-slate-700">Drag & drop your file here, or click to browse</p>
                                <p className="text-xs text-slate-400">Accepted formats: .csv, .xlsx</p>
                            </div>
                        )}
                    </div>

                    {lastResult && (
                        <div className="rounded-lg bg-green-50 border border-green-200 p-4 flex items-center gap-3" data-testid="price-update-success">
                            <CheckCircle className="w-5 h-5 text-green-600 shrink-0" />
                            <div>
                                <p className="font-medium text-green-800">{lastResult.updated_count} product prices updated successfully</p>
                                <p className="text-xs text-green-700">{new Date(lastResult.timestamp).toLocaleString()}</p>
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                /* ---------------- Preview screen ---------------- */
                <div className="bg-white rounded-xl p-6 border border-slate-100 space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div>
                            <h3 className="text-lg font-bold text-slate-900">Review price changes</h3>
                            <p className="text-sm text-slate-500 flex items-center gap-1">
                                <FileSpreadsheet className="w-4 h-4" /> {preview.fileName}
                            </p>
                        </div>
                        <p className="text-sm font-medium text-slate-700" data-testid="price-preview-summary">
                            <span className="text-green-700">{preview.summary.will_update} prices will be updated</span>
                            {', '}
                            <span className="text-red-600">{preview.summary.not_found} products not found and skipped</span>
                        </p>
                    </div>

                    <div className="overflow-x-auto border border-slate-200 rounded-lg max-h-[480px]">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 sticky top-0">
                                <tr className="text-left text-slate-500">
                                    <th className="font-medium px-3 py-2">Product Name</th>
                                    <th className="font-medium px-3 py-2 text-right">Current Price</th>
                                    <th className="font-medium px-3 py-2 text-right">New Price</th>
                                    <th className="font-medium px-3 py-2">Change</th>
                                </tr>
                            </thead>
                            <tbody>
                                {preview.rows.map((row, i) => {
                                    const notFound = row.status === 'not_found';
                                    return (
                                        <tr key={i} className={`border-t border-slate-100 ${notFound ? 'bg-red-50' : ''}`} data-testid={`price-preview-row-${i}`}>
                                            <td className="px-3 py-2 text-slate-800">
                                                {row.product_name}
                                                {notFound && (
                                                    <span className="block text-xs text-red-600" data-testid={`price-row-notfound-${i}`}>
                                                        Product not found — will be skipped
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-3 py-2 text-right text-slate-600">{money(row.current_price)}</td>
                                            <td className="px-3 py-2 text-right font-medium text-slate-900">{money(row.new_price)}</td>
                                            <td className="px-3 py-2"><ChangeIndicator status={row.status} /></td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <div className="flex justify-end gap-3">
                        <Button variant="outline" onClick={() => setPreview(null)} data-testid="cancel-upload-btn">
                            Cancel
                        </Button>
                        <Button
                            onClick={handleConfirm}
                            disabled={applying || preview.summary.will_update === 0}
                            className="bg-green-600 text-white hover:bg-green-700 gap-2"
                            data-testid="confirm-update-prices-btn"
                        >
                            {applying && <Loader2 className="w-4 h-4 animate-spin" />}
                            Confirm & Update Prices
                        </Button>
                    </div>
                </div>
            )}

            {/* Audit log */}
            <div className="bg-white rounded-xl p-6 border border-slate-100">
                <h3 className="font-semibold text-slate-900 flex items-center gap-2 mb-4">
                    <History className="w-4 h-4 text-[#1B4D3E]" />
                    Recent Price Updates
                </h3>
                {history.length === 0 ? (
                    <p className="text-sm text-slate-400" data-testid="price-history-empty">No price updates yet.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="price-history-table">
                            <thead>
                                <tr className="text-left text-slate-500 border-b border-slate-200">
                                    <th className="font-medium px-3 py-2">Date</th>
                                    <th className="font-medium px-3 py-2 text-right">Products Updated</th>
                                    <th className="font-medium px-3 py-2">Updated By</th>
                                </tr>
                            </thead>
                            <tbody>
                                {history.map((ev, i) => (
                                    <tr key={ev.batch_id || i} className="border-b border-slate-100" data-testid={`price-history-row-${i}`}>
                                        <td className="px-3 py-2 text-slate-700">{new Date(ev.timestamp).toLocaleString()}</td>
                                        <td className="px-3 py-2 text-right font-medium text-slate-900">{ev.updated_count}</td>
                                        <td className="px-3 py-2 text-slate-600">{ev.admin_name || ev.admin_email}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};
